import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import AppModal from '../common/AppModal';
import { Share, Trash, BracesAsterisk } from 'react-bootstrap-icons';
import CircularProgressBar from '../common/circular-progress';
import useLocalization from '../../hooks/useLocalization';
import { reportService } from '../../services/reportServices';
import EditorWithPlaceholderSidebar from '../editorjs-with-placeholder-sidebar';
import AlertModal from '../common/AlertModal';
import SwitchInput from '../common/SwitchInput';
import { Badge, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';

function EditApartmentReportModal({ show, onHide, apartmentReport, onChange, onDelete, onUpdateStatus }) {
  const { isRTL, translate } = useLocalization();
  const [reportMeta, setReportMeta] = useState(apartmentReport);

  const hasPlaceholder = useMemo(() => {
    const regex = /{{\s*([^}]+)\s*}}/g;
    const matches = reportMeta?.body?.match(regex);
    return matches?.length > 0;
  }, [reportMeta?.body]);

  const replaceReportBodyWithPlaceholder = async () => {
    setReportMeta({ ...reportMeta, progress: true });
    const { response, error } = await reportService.replaceReportBodyWithPlaceholder(apartmentReport._id, {
      body: reportMeta?.body || apartmentReport?.body
    });
    if (error) {
      toast.error('Error replacing report body with placeholder');
      setReportMeta({ ...reportMeta, progress: false });
      return;
    }

    // Update both the local state and parent component
    setReportMeta({
      ...reportMeta,
      body: response.data.body,
      aiResponse: response.data.aiResponse,
      progress: false
    });
    onChange(response.data);
  };

  const updateReport = async () => {
    if (apartmentReport?.status === 'complete') {
      return;
    }
    setReportMeta({ ...reportMeta, progress: true });

    const { error, response } = await reportService.updateReport(apartmentReport._id, {
      status: reportMeta?.status,
      body: reportMeta.body
    });
    if (error) {
      setReportMeta({ ...reportMeta, progress: false });
      toast.error(translate('error_updating_report'));
      return;
    }

    setReportMeta({ ...reportMeta, progress: false });
    onChange(response.data);
    toast.success(translate('report_updated_successfully'));
  };

  const hasContentChanged = useMemo(() => {
    return reportMeta?.body !== apartmentReport?.body;
  }, [reportMeta?.body, apartmentReport?.body]);

  const handleSave = async (closeAfterSave = false) => {
    const originalStatus = reportMeta?.status;
    try {
      if (reportMeta?.body && apartmentReport?.body) {
        await updateReport(reportMeta);
        apartmentReport.body = reportMeta.body;
        if (closeAfterSave) {
          onHide();
        }
      }
    } catch (error) {
      setReportMeta({ ...reportMeta, status: originalStatus });
      toast.error(translate('save_failed'));
    }
  };

  useEffect(() => {
    if (apartmentReport) {
      setReportMeta({ ...reportMeta, status: apartmentReport.status });
    }
  }, [apartmentReport?.status]);

  const deleteReport = async () => {
    setReportMeta({ ...reportMeta, deleting: true });
    const { error } = await reportService.deleteReport(apartmentReport._id);
    if (error) {
      toast.error('Error deleting report');
      setReportMeta({ ...reportMeta, deleting: false, wantToDelete: false });
      return;
    }
    toast.success('Report deleted successfully');
    setReportMeta({});
    onHide();
    onDelete();
  };

  const updateReportStatus = async () => {
    setReportMeta({ ...reportMeta, updating: true });
    const { error, response } = await reportService.updateReport(apartmentReport._id, {
      status: reportMeta?.status,
      body: reportMeta?.body || apartmentReport?.body
    });
    if (error) {
      toast.error('Error updating report status');
      setReportMeta({ ...reportMeta, updating: false, status: apartmentReport?.status, wantToTogglePublish: false });
      return;
    }
    toast.success('Report status updated successfully');
    setReportMeta({
      ...reportMeta,
      updating: false,
      body: reportMeta?.body || apartmentReport?.body,
      wantToTogglePublish: false
    });
    onUpdateStatus(reportMeta?.status);
    onChange(response.data);
  };

  const isPublished = useMemo(() => apartmentReport?.status === 'complete', [apartmentReport?.status]);

  const renderExtrasInHeader = () => {
    let status = 'saving';
    if (reportMeta?.deleting) status = 'deleting';
    if (reportMeta?.updating) status = 'updating';

    return (
      <div style={{ height: 24 }} className={`${isRTL ? 'ms-4' : 'me-4'} d-flex align-items-center`}>
        {Boolean(reportMeta?.progress || reportMeta?.deleting || reportMeta?.updating) ? (
          <div className={'d-flex align-items-center gap-2'}>
            <CircularProgressBar size={1.5} />
            <span className="smallFont">{translate(status)}</span>
          </div>
        ) : (
          <div className={'d-flex align-items-center gap-4'}>
            {hasPlaceholder && (
              <Button
                size="sm"
                className="d-flex align-items-center gap-1 hover-text-white m-0"
                variant={'outline-success'}
                onClick={replaceReportBodyWithPlaceholder}
                disabled={reportMeta?.progress}
              >
                <BracesAsterisk size={16} />
                <h6 className="mb-0 smallFont">{translate('fill_placeholder')}</h6>
              </Button>
            )}

            <SwitchInput
              falseValue="draft"
              trueValue="complete"
              threeState={false}
              value={reportMeta?.status}
              onChange={status => setReportMeta({ ...reportMeta, wantToTogglePublish: true, status })}
              label={isPublished ? translate('mark_as_draft') : translate('publish')}
              offColor="#808080"
            />

            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip>{isPublished ? translate('share') : translate('report_is_in_draft_mode')}</Tooltip>}
            >
              <Share size={16} color={!isPublished ? 'gray' : 'black'} className={isPublished ? 'hover' : ''} />
            </OverlayTrigger>

            <Trash
              onClick={() => setReportMeta({ ...reportMeta, wantToDelete: true })}
              size={16}
              className="hover text-danger"
            />

            <div className="d-flex align-items-center gap-2">
              <Button
                size="sm"
                variant="primary text-white"
                disabled={!hasContentChanged}
                onClick={() => handleSave(false)}
              >
                {translate('save')}
              </Button>

              <Button
                size="sm"
                variant="success text-white"
                disabled={!hasContentChanged}
                onClick={() => handleSave(true)}
              >
                {translate('save_and_close')}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <AppModal
        modalBodyClassName="p-0 overflow-hidden"
        fullscreen
        show={show}
        onHide={onHide}
        title={translate('apartment_report')}
        renderMoreInHeader={renderExtrasInHeader}
        showCloseButton={!reportMeta?.progress}
        titleBadge={() => (
          <Badge className="fw-normal" variant="light" bg={`${isPublished ? 'success' : 'gray'}`}>
            {isPublished ? translate('published') : translate('draft')}
          </Badge>
        )}
      >
        <div>
          {apartmentReport?.body && (
            <EditorWithPlaceholderSidebar
              documentVariables={apartmentReport?.aiResponse}
              data={apartmentReport?.body}
              id={apartmentReport?._id}
              onChange={updatedReportMeta => setReportMeta({ ...reportMeta, ...updatedReportMeta })}
              setProgress={value => setReportMeta({ ...reportMeta, updating: value, progress: value })}
              readOnly={reportMeta?.status === 'complete'}
            />
          )}
        </div>
      </AppModal>
      <AlertModal
        show={reportMeta?.wantToDelete}
        onHide={() => setReportMeta({ ...reportMeta, wantToDelete: false })}
        alertText={translate('delete_report_alert')}
        continueButtonVariant="danger"
        continueButtonText={translate('yes')}
        onContinueClick={deleteReport}
        showProgress={reportMeta?.deleting}
        progressText={translate('deleting')}
      />
      <AlertModal
        show={reportMeta?.wantToTogglePublish}
        onHide={() => setReportMeta({ ...reportMeta, wantToTogglePublish: false, status: apartmentReport?.status })}
        alertText={!isPublished ? translate('publish_report_alert') : translate('unpublish_report_alert')}
        continueButtonVariant={!isPublished ? 'success text-white' : 'primary text-white'}
        continueButtonText={!isPublished ? translate('publish') : translate('mark_as_draft')}
        onContinueClick={updateReportStatus}
      />
    </>
  );
}

export default EditApartmentReportModal;
